/**
 * Lightweight wrapper around the Vibration API for mobile-web haptic
 * feedback. Mimics the iOS / Android "tap" feedback so important
 * interactions (save, delete confirm, scheduler-fired toast) feel like
 * a native app.
 *
 * Sharp edges:
 *   - Desktop browsers + iOS Safari (no Vibration API outside Android
 *     Chrome / Edge) silently no-op. Calling code should never depend
 *     on side effects.
 *   - We respect `prefers-reduced-motion: reduce` to avoid surprise
 *     buzzes for users who opted out of motion (some accessibility
 *     guides treat haptics as motion).
 *   - Each preset is a single, short pulse — no patterns longer than
 *     30 ms, since Android throttles long pulses and Chrome enforces
 *     a user-gesture requirement that's already satisfied at the
 *     button-tap site.
 */

type Preset = 'tap' | 'success' | 'warn' | 'error';

const DURATIONS_MS: Record<Preset, number | number[]> = {
  tap: 8,
  success: 14,
  warn: [10, 60, 10],
  error: [18, 70, 18, 70, 18],
};

let _disabled: boolean | null = null;

function reducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Whether this device/browser can deliver haptic feedback right now: the
 * Vibration API must exist AND the user must not have opted out of motion.
 *
 * Exposed so settings (and any other surface) can present the feature
 * honestly — e.g. iOS Safari has no Vibration API, so a haptic toggle there
 * would be a false promise. This is the device-capability check; it does NOT
 * consider the test-only disable flag.
 */
export function hapticsSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.vibrate !== 'function') return false;
  return !reducedMotion();
}

function isAvailable(): boolean {
  if (_disabled === true) return false;
  return hapticsSupported();
}

export function haptic(preset: Preset = 'tap'): void {
  if (!isAvailable()) return;
  try {
    navigator.vibrate(DURATIONS_MS[preset]);
  } catch {
    /* swallow — never bubble vibration errors into UI flows */
  }
}

/** Test helper — force `isAvailable` to false. */
export function _disableForTests(): void {
  _disabled = true;
}

export function _resetForTests(): void {
  _disabled = null;
}
